<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import type { RelicItemType } from '@/types/relic'
import { computed, ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { getRelicName } from '@/core/relic'
import RelicInfo from '@/views/item/components/RelicInfo.vue'
import { relicNeedStarMap } from '../data'
import { equipQualityText } from '@/views/item/data'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'

const userStore = useUserStore()

const itemList = computed(() => {
  return userStore.getItemRelicInfoList.filter((item) => {
    if (item.isLock || !item.count || item.quality === 'antiquity') return false
    if (distillIdList.value.includes(item.id)) return false
    return true
  })
})
const distillIdList = ref<string[]>([])
const distillItemList = computed(() => {
  return userStore.getItemRelicInfoList
    .filter((item) => {
      return distillIdList.value.includes(item.id)
    })
    .map((item) => ({
      ...item,
      useCount: 1,
    }))
})
function handleBatchChoice(quality: string) {
  let choiceQuality: string[] = []
  if (quality === 'normal') {
    choiceQuality = ['normal']
  } else if (quality === 'race') {
    choiceQuality = ['normal', 'race']
  } else if (quality === 'epic') {
    choiceQuality = ['normal', 'race', 'epic']
  }

  const resList = itemList.value.filter((item) => choiceQuality.includes(item.quality)).map((item) => item.id)
  distillIdList.value = [...new Set([...resList, ...distillIdList.value])]
}

const relicInfo = ref<RelicItemType>()
const isShowRelicInfo = ref(false)
async function handleAction(equipItem: RelicItemType, type: 'putIn' | 'takeOut') {
  if (isShowRelicInfo.value) return (relicInfo.value = equipItem)
  const { id } = equipItem
  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'putIn') {
    distillIdList.value.push(id)
  } else {
    const index = distillIdList.value.findIndex((item) => item === id)
    if (index === -1) return
    distillIdList.value.splice(index, 1)
  }
}

const isStart = ref(false)
function handleStart() {
  isStart.value = true
  useRelicList.value = distillItemList.value.map((item) => [item.id, 0])
}
function handleClear() {
  distillIdList.value = []
  isStart.value = false
}

const useRelicList = ref<[string, number][]>([])
const distillResStar = computed(() => {
  return useRelicList.value.reduce((pre, [id, count]) => {
    const relicItemInfo = userStore.relicList.find((relic) => relic.id === id)
    if (!relicItemInfo) return pre
    const res = (relicNeedStarMap[relicItemInfo.quality] * count) / 10
    return pre + res
  }, 0)
})
async function handleDistill() {
  if (!distillResStar.value) return ElMessage.warning('未选取任何圣物进行提炼')
  ElMessageBox.confirm(`<p class="affix">确定进行提炼吗？该操作将导致您所选择的圣物<span class="hp">销毁对应数量</span>，请务必谨慎</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<{ resStar: number; relicList: RelicItemType[] }>({
        type: 'distillRelic',
        data: { relicList: useRelicList.value },
      })
      userStore.consumables.relicStar += res.resStar
      userStore.relicList = res.relicList
      distillIdList.value = []
      isStart.value = false
    })
    .catch(() => {})
}
</script>

<template>
  <div class="distill">
    <p class="header">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="18"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>提炼说明</h4>
          <p>1. 通用提炼圣物，您可以将多余的圣物兑换为<span class="relic">圣物碎星</span>，用于图鉴中的合成功能。</p>
          <p>2. 您需要先将用于提炼的圣物放入材料台，而后点击<span class="info">开始</span>，之后在弹窗中选择具体的提炼圣物数量。</p>
          <p>3. 获得的圣物碎星与圣物的品质挂钩。</p>
        </div>
      </el-popover>
      <span>✨ {{ formatNumberWithCommas(userStore.consumables.relicStar) }}</span>
    </p>
    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>圣物背包</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看圣物信息：</span>
            <el-switch v-model="isShowRelicInfo" size="small" @change="relicInfo = undefined" />
          </div>
          <el-select size="small" style="margin: 0 5px; width: calc(100% - 10px)" placeholder="批量选择">
            <el-option v-for="item of ['normal', 'race', 'epic']" :value="item" @click="handleBatchChoice(item)">
              <span :class="item" style="font-size: 12px">{{ `${equipQualityText[item]}及以下` }}</span>
            </el-option>
          </el-select>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'putIn')"
                  :style="{ borderColor: item?.id && relicInfo?.id === item.id ? 'red' : '' }"
                >
                  <span :class="item.quality" v-html="getRelicName(item.relicId, item.count)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowRelicInfo">
          <h4>圣物信息</h4>
          <template v-if="relicInfo">
            <RelicInfo :relic-info="relicInfo" :show-action="false" :show-lock="false" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="action border-wrap">
          <h4>操作</h4>
          <el-button type="primary" plain size="small" @click="handleStart">开始</el-button>
          <el-button type="warning" plain size="small" @click="handleClear">清空材料台</el-button>
        </div>
        <div class="border-wrap">
          <h4>材料台</h4>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of distillItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'takeOut')"
                  :style="{ borderColor: item?.id && relicInfo?.id === item.id ? 'red' : '' }"
                >
                  <span :class="item.quality" v-html="getRelicName(item.relicId, item.count)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>

    <el-dialog v-model="isStart" width="100%" style="max-width: 500px" destroy-on-close>
      <h2>请选择提炼数量</h2>

      <div>
        <el-button type="primary" size="small" plain @click="useRelicList = distillItemList.map((item) => [item.id, item.count])"
          >全选最大值</el-button
        >
        <el-button
          type="success"
          size="small"
          plain
          @click="useRelicList = distillItemList.map((item) => [item.id, item.count > 50 ? item.count - 50 : 0])"
          >选取溢出部分</el-button
        >
        <el-button type="warning" size="small" plain @click="useRelicList = distillItemList.map((item) => [item.id, 0])">重置</el-button>
      </div>
      <div style="max-height: 40vh; overflow-y: auto; overflow-x: hidden">
        <el-row :gutter="10">
          <el-col :span="24" v-for="(item, index) of distillItemList" :key="item.id" style="padding: 15px">
            <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
              <template #reference>
                <div>
                  <CommonBtn size="small" style="width: 100%">
                    <span :class="item.quality">
                      {{ getRelicName(item.relicId, item.count) }}
                    </span>
                  </CommonBtn>
                </div>
              </template>
              <RelicInfo :relic-info="item" :show-action="false" />
            </el-popover>
            <el-slider v-model="useRelicList[index][1]" size="small" :min="0" :max="item.count" show-input style="margin: 5px 0"></el-slider>
          </el-col>
        </el-row>
      </div>

      <p>提炼结果：{{ formatNumberWithCommas(distillResStar) }} ✨圣物碎星</p>

      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="handleDistill" type="primary">确定</el-button>
          <el-button plain @click="isStart = false">取消</el-button>
        </p>
      </template>
    </el-dialog>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.distill {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .header {
    display: flex;
    justify-content: space-between;
    margin: 0 0 5px 0;
    padding: 0 15px;
    font-size: 14px;
  }
  .item-filter {
    margin-bottom: 10px;
    display: flex;
    .el-button {
      margin-left: 10px;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
      &.action {
        height: auto;
        margin-bottom: 10px;
        padding: 0 10px 10px 10px;
        flex-shrink: 0;
        .el-button {
          margin-bottom: 10px;
          margin-left: 0;
        }
      }
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
